<script setup lang="ts">
import {
    faCircle,
    faCirclePause,
    faEdit,
    faTimer,
    faWarning,
    faFlagCheckered,
    faRepeat,
    faClock,
    faCheckCircle,
    faTrash,
    faQuestion,
    faMinus,
    faCircleMinus,
    faTimes,
    faHandPointer,
    faShippingTimed,
    faMailbox,
    faExclamationTriangle,
    faRedo,
    faUndo,
    faEye,
    faShippingFast,
    faFlagAlt,
    faUserCheck,
    faPhoneSlash,
    faTriangleExclamation,
    faCircleQuestion,
    faCommentSlash,
    faFilterCircleXmark,
} from "@fortawesome/pro-solid-svg-icons";
import type { Automation } from "~/models/Automation";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faReply, faSpinnerThird } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{
    status?: string;
    name?: string;
    automation?: Automation | { status: string; name?: string };
    iconOnly?: boolean;
    forSurvey?: boolean;
    size?: any
}>();

const STATUSES = computed(() => ({
    active: [faCircle, "text-hd-secondary-original"],
    running: [faCircle, "text-hd-secondary-original"],
    sending: [faCircle, "text-hd-secondary-original"],
    provisioning: [faSpinnerThird],
    paused: [faCirclePause, "text-hd-accent-one-original"],
    scheduled: props.forSurvey
        ? [faClock, "text-orange-500"]
        : [faRepeat, "text-hd-neutral-500"],
    finished: [faFlagCheckered],
    sent: [faCheckCircle, "text-hd-secondary-original"],
    draft: [faEdit, "text-hd-neutral-500"],
    error: [faWarning, "text-red-500"],
    warning: [faWarning, "text-yellow-400"],
    suspended: [faWarning, "text-hd-accent-two-original"],
    cancelled: [faCircleMinus, "text-red-500"],
    timeout: [faTimer, "text-hd-accent-two-original"],
    pending: [faClock, "text-hd-neutral-500"],
    deleted: [faTrash, "text-red-500"],
    migrated: [faMinus, "text-indigo-500"],
    banned: [faMinus, "text-red-500"],
    restricted: [faWarning, "text-yellow-400"],
    rate_limited: [faWarning, "text-yellow-400"],
    flagged: [faWarning, "text-red-500"],
    connected: [faCircle, "text-hd-secondary-original"],
    disconnected: [faQuestion, "text-red-500"],
    unknown: [faQuestion, "text-hd-neutral-500"],
    unverified: [faQuestion, "text-hd-neutral-500"],
    attempted: [faCircle, "text-hd-neutral-500"],
    scheduling: [faClock, "text-orange-500"],
    email_bounce: [faTimes, "text-red-500"],
    email_click: [faHandPointer, "text-blue-500"],
    email_deferred: [faShippingTimed, "text-yellow-500"],
    email_delivered: [faMailbox, "text-green-500"],
    email_dropped: [faExclamationTriangle, "text-red-500"],
    email_scheduling: [faClock, "text-orange-500"],
    email_group_resubscribe: [faRedo, "text-green-500"],
    email_group_unsubscribe: [faUndo, "text-red-500"],
    email_unsubscribe: [faUndo, "text-red-500"],
    email_open: [faEye, "text-blue-500"],
    email_processed: [faShippingFast, "text-green-500"],
    email_spamreport: [faFlagAlt, "text-red-500"],
    email_account_status_change: [faUserCheck, "text-green-500"],
    email_response: [faReply, "text-green-500"],
    phone_number_not_found: [faPhoneSlash, "text-red-500"],
    invalid_parameter: [faTriangleExclamation, "text-orange-500"],
    receiver_did_not_reply: [faCommentSlash, "text-yellow-500"],
    other: [faCircleQuestion, "text-gray-500"],
    completed: [faCheckCircle, "text-green-500"],
    failed: [faCircleMinus, "text-red-500"],
    filtered_out: [faFilterCircleXmark, "text-orange-500"],
}));

const status = computed(
    () => props.status ?? props.automation?.status ?? "unknown",
);

const name = computed(
    () => props.name ?? props.automation?.name ?? props.automation?.id ?? "",
);

const status_style = computed(
    () =>
        STATUSES.value[props.status ?? props.automation?.status ?? "unknown"] ??
        STATUSES.value["unknown"],
);

const spinning = computed(() => {
    return ["provisioning"].includes(status.value);
});
</script>

<template>
    <div :class="iconOnly ? undefined : 'flex items-center gap-2'">
        <FontAwesomeIcon
            :icon="status_style[0]"
            :class="status_style[1]"
            :spin="spinning.value"
            :size="size"
        />
        <span v-if="!iconOnly && name" class="max-w-lg truncate">{{ name }}</span>
    </div>
</template>
